import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import styled, { css } from 'styled-components';

import { SharingModal, SharingModalApi } from '#/components/Modal/SharingModal';
import {
  ShowMoreModal,
  ShowMoreModalApi,
} from '#/components/Modal/ShowMoreModal';
import { NotFound } from '#/components/Status/NotFound';
import { devtoolsApi } from '#/dev/devtools';
import { BuildDynamicUiParams } from '#/dynamic/components/defaultComponents';
import { useActions } from '#/hooks/actions/useActions';
import { useConfig } from '#/hooks/useConfig';
import { PageContext, useDataContext } from '#/hooks/useDataContext';
import { usePageGuard } from '#/hooks/usePageGuard';
import { useResponsiveFormFactor } from '#/hooks/useResponsiveValue';
import { usePageController } from '#/pages/PageController.hook';
import { PageRenderer } from '#/pages/PageRenderer';
import { PageAliasesConfig } from '#/service/config/common.types';
import { MenuBackgroundStyle } from '#/service/constants';
import { useHistoryStore } from '#/store/history/history.store';

import {
  PageContainerProps,
  PageControllerProps,
  PageParams,
} from './pageTypes';

/** Controller for Dynamic UI layout based pages/views. */
export const PageController: FC<PageControllerProps> = ({
  pageId: pageIdProp,
  defaultContext = {},
  fullPage = true,
  screenType,
  screenTitle,
}) => {
  const params = useParams<PageParams>();
  const formFactor = useResponsiveFormFactor();
  const { featureFlags, pages } = useConfig();
  const { isHistoryFetched } = useHistoryStore();

  const pageId = useMemo(() => {
    const pageIdOrAlias = pageIdProp ?? params.pageId;
    const normalizedAlias =
      pageIdOrAlias?.toUpperCase() as keyof PageAliasesConfig;

    return (pageIdOrAlias && pages[normalizedAlias]) || pageIdOrAlias;
  }, [pageIdProp, params.pageId, pages]);

  const sharingModalRef = useRef<SharingModalApi>(null);
  const showMoreModalRef = useRef<ShowMoreModalApi>(null);

  const { getPageContext } = useDataContext();
  const { layout, error, theme, translations, parentalSystem, v2Templates } =
    usePageController({ pageId, defaultContext, screenType, screenTitle });
  usePageGuard();

  // prepare the params needed for the `buildDynamicUi` function, but don't call it yet
  // see the explanation in `PageRenderer.tsx`
  const buildDynamicUiParams: BuildDynamicUiParams = useMemo(
    () => ({
      fontConfig: theme.fonts,
      translations,
      parentalSystem,
      // Hover cards are on for everyone bar the customers that opt out.
      hoverCardsEnabled: !featureFlags.hideHoverCards,
      formFactor,
      v2Templates,
    }),
    [
      featureFlags.hideHoverCards,
      formFactor,
      parentalSystem,
      theme.fonts,
      translations,
      v2Templates,
    ]
  );

  const templateProps = layout?.templates?.[formFactor]?.properties;
  const hasTopPadding =
    fullPage &&
    (theme?.menu?.background === MenuBackgroundStyle.Opaque ||
      !!templateProps?.topContainerShouldNotOverlap);

  // context has to be in a state, because we'll need to update it to force a DynUi re-render.
  // no initial value, because we don't have layout on first render.
  const [context, setContext] = useState<PageContext>();
  // create the page context, and put in state
  useEffect(() => {
    setContext((prevState) =>
      getPageContext(prevState, defaultContext, layout)
    );
  }, [defaultContext, getPageContext, layout]);
  // actions will use this to force a DynUi re-render by changing the reference of the context object.
  // ie: create a new object but keep the contents of the old one.
  const updatePageContext = useCallback(
    (propsToUpdate: { [key: string]: any } = {}) => {
      setContext((prev) => (prev ? { ...prev, ...propsToUpdate } : prev));
    },
    []
  );
  // passing in the update function to the actions hook so any action can use it.
  const { actions } = useActions({
    sharingModalRef,
    showMoreModalRef,
    updatePageContext,
  });
  // updating page context when isHistoryFetched changes its value
  // to force DynUI re-fetch content on currently viewed page.
  useEffect(() => {
    updatePageContext?.({ isHistoryFetched });
  }, [isHistoryFetched, updatePageContext]);

  console.debug('PageController - Layout with data', layout);
  console.debug('PageController - v2Templates', v2Templates);

  if (process.env.goal === 'local') {
    // update devtools
    devtoolsApi.setLayout(layout);
    devtoolsApi.setContexts({ page: context });
    devtoolsApi.setStoryPageDef({
      storyTitle: 'Untitled story',
      layout,
      context,
      fontConfig: theme.fonts,
      translations,
      parentalSystem,
      formFactor,
      v2Templates,
      theme,
    });
  }

  const cacheKey = `${pageId}-${formFactor}-${parentalSystem}`;

  if (error) {
    if (error.statusCode === 404) {
      return <NotFound />;
    }

    throw error;
  }

  return (
    <PageContainer $hasTopPadding={hasTopPadding} data-testid="pageContainer">
      <PageRenderer
        actions={actions}
        buildDynamicUiParams={buildDynamicUiParams}
        cacheKey={cacheKey}
        context={context}
        layout={layout}
      />
      <SharingModal apiRef={sharingModalRef} />
      <ShowMoreModal apiRef={showMoreModalRef} />
    </PageContainer>
  );
};

PageController.displayName = 'PageController';

export const EmptyPage = () => <div />;

const PageContainer = styled.div<PageContainerProps>`
  ${({ $hasTopPadding }) => {
    if (!$hasTopPadding) {
      return;
    }

    return css`
      padding-top: var(--menuHeight, 0);
      min-height: calc(100vh - var(--menuHeight, 0rem));
    `;
  }};
`;
